import { TotalSupplyAbi } from '../abis/shared'
import { VedaAccountantGetRateAbi } from '../abis/veda'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, rescaleDecimals, toBigInt, type LstReader } from './shared'

/**
 * Veda Labs BoringVault reader — used for ether.fi's eBTC.
 *
 * The vault share token has no ERC-4626 surface; its exchange rate
 * lives on the Accountant's `getRate()`, denominated in the base
 * asset's decimals (8 for the BTC vaults). Two calls:
 *   1. `vault.totalSupply()`
 *   2. `Accountant.getRate()` — assets-per-share, base-asset decimals.
 *
 * The rate is normalised to the 1e18 value-ratio scale the rest of the
 * pipeline expects via `10 ** (18 - underlyingDecimals)` (the Accountant
 * denominates the rate in the *underlying* asset's decimals). Requires
 * `readerExtras.vedaAccountant`; without it the reader degrades to a
 * bare `totalSupply()` at a 1:1 rate so the entry still surfaces.
 *
 * `totalAssets` is reported in the underlying asset's decimals, so the
 * share-denominated `totalSupply * rate` product is rescaled to the
 * underlying when share ≠ underlying decimals.
 */
export const readerVedaAccountant = (entry: LstRegistryEntry): LstReader => {
  const accountant = entry.readerExtras?.vedaAccountant
  const shareDec = entry.decimals
  const underlyingDec = entry.underlyingDecimals ?? entry.decimals
  if (!accountant) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: rescaleDecimals(totalSupply, shareDec, underlyingDec),
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  const scale = 10n ** BigInt(18 - underlyingDec)
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: accountant, name: 'getRate', params: [] },
    ],
    abis: [TotalSupplyAbi, VedaAccountantGetRateAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const rawRate = toBigInt(rate)
      if (totalSupply === undefined || rawRate === undefined) return undefined
      const exchangeRate = rawRate * scale
      return {
        totalAssets: rescaleDecimals(
          (totalSupply * exchangeRate) / ONE_E18,
          shareDec,
          underlyingDec,
        ),
        totalSupply,
        exchangeRate,
      }
    },
  }
}
